import { coalesce } from "../lib/coalesce";
import { shallowMerge } from "../lib/shallow-merge";
function sortBreakpoints(breakpoints) {
  return breakpoints.sort((a, b) => (b?.minWidth ?? 0) - (a?.minWidth ?? 0));
}
const getBaseBreakpoint = (breakpoints) => {
  const breakpoint = sortBreakpoints(breakpoints)[0];
  if (breakpoint == null)
    throw new Error(`Cannot get base breakpoint.`);
  return breakpoint;
};
const getBreakpoint = (breakpoints, breakpointId) => {
  const breakpoint = breakpoints.find(({ id }) => id === breakpointId);
  if (breakpoint == null)
    throw new Error(`Unrecognized breakpoint ID: "${breakpointId}".`);
  return breakpoint;
};
function findBreakpointOverride(breakpoints, values = [], deviceId, strategy = defaultStrategy) {
  const value = values.find(({ deviceId: d }) => d === deviceId);
  const fallbacks = breakpoints.slice(0, breakpoints.findIndex((d) => d.id === deviceId) + 1).reverse().map((d) => values.find((v) => v.deviceId === d.id)).filter((override) => Boolean(override));
  return value != null || fallbacks.length > 0 ? strategy(value, fallbacks) : void 0;
}
function defaultStrategy(value, fallbacks) {
  return value || fallbacks[0];
}
function shallowMergeFallbacks(value, fallbacks) {
  return [value, ...fallbacks].filter((override) => Boolean(override)).reduce((a, b) => ({
    deviceId: a.deviceId || b.deviceId,
    value: shallowMerge(a.value, b.value)
  }));
}
function mergeOrCoalesce(a, b) {
  if (typeof a === "object" && a !== null && !Array.isArray(a) && typeof b === "object" && b !== null && !Array.isArray(b)) {
    return shallowMerge(a, b);
  }
  return coalesce(a, b);
}
function mergeOrCoalesceFallbacks(value, fallbacks) {
  return [value, ...fallbacks].filter((override) => Boolean(override)).reduce((a, b) => ({
    deviceId: a.deviceId || b.deviceId,
    value: mergeOrCoalesce(a.value, b.value)
  }));
}
function join(breakpoints, responsiveValues, joinFn, strategy) {
  return breakpoints.map(({ id }) => id).map((deviceId) => {
    const value = joinFn(
      responsiveValues.map((responsiveValue) => {
        const deviceValue = responsiveValue && findBreakpointOverride(
          breakpoints,
          responsiveValue,
          deviceId,
          strategy
        );
        return deviceValue == null ? void 0 : deviceValue.value;
      })
    );
    if (value == null)
      return null;
    return { deviceId, value };
  }).filter(
    (override) => Boolean(override)
  );
}
function mergeResponsiveValues(breakpoints, source, override) {
  const devices = [
    ...new Set(
      source.map(({ deviceId }) => deviceId).concat(override.map(({ deviceId }) => deviceId))
    )
  ];
  return devices.map((deviceId) => ({
    deviceId,
    value: {
      ...(findBreakpointOverride(breakpoints, source, deviceId) || { value: {} }).value,
      ...(findBreakpointOverride(breakpoints, override, deviceId, (v) => v) || {
        value: {}
      }).value
    }
  }));
}
function findNextFallback(breakpoints, value, deviceId, activeDeviceId, fallbackStrategy) {
  const deviceOverride = findBreakpointOverride(
    breakpoints,
    value.filter((v) => v.deviceId !== activeDeviceId),
    deviceId,
    fallbackStrategy
  );
  return deviceOverride != null ? getBreakpoint(breakpoints, deviceOverride.deviceId) : null;
}
const getBreakpointMediaQuery = (breakpoint) => {
  const parts = ["@media only screen"];
  if (breakpoint.minWidth != null) {
    parts.push(`(min-width: ${breakpoint.minWidth}px)`);
  }
  if (breakpoint.maxWidth != null) {
    parts.push(`(max-width: ${breakpoint.maxWidth}px)`);
  }
  return parts.join(" and ");
};
const getViewportStyle = (breakpoints, deviceId) => {
  const device = getBreakpoint(breakpoints, deviceId);
  return {
    width: device.viewportWidth != null ? device.viewportWidth : "100%",
    minWidth: device.minWidth
  };
};
export {
  findBreakpointOverride,
  findNextFallback,
  getBaseBreakpoint,
  getBreakpoint,
  getBreakpointMediaQuery,
  getViewportStyle,
  join,
  mergeOrCoalesceFallbacks,
  mergeResponsiveValues,
  shallowMergeFallbacks
};
//# sourceMappingURL=breakpoints.js.map